import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { runAIPrompt } from "@/lib/ai";
import { Gauge, StatusBadge } from "@/components/common/Bits";
import {
  Users, Scale, Clock, Gavel, ListChecks, FileText, Upload, Sparkles,
  AlertTriangle, TrendingUp, CalendarDays, ArrowUpRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

function StatCard({ label, value, icon: Icon, testid, sub }) {
  return (
    <div className="surface-card surface-card-hover p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</div>
        <Icon className="size-4 text-muted-foreground" strokeWidth={1.5}/>
      </div>
      <div className="stat-num text-3xl">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">{sub}</div>}
    </div>
  );
}

function QuickAction({ label, icon: Icon, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group relative surface-card surface-card-hover p-4 flex items-center gap-3 text-left tracing-beam"
    >
      <div className="size-9 rounded-md bg-[hsl(var(--gold)/0.12)] gold-border border flex items-center justify-center">
        <Icon className="size-4 gold-text" strokeWidth={1.5}/>
      </div>
      <span className="text-[13px] font-medium flex-1">{label}</span>
      <ArrowUpRight className="size-4 text-muted-foreground group-hover:gold-text transition-colors"/>
    </button>
  );
}

export default function Dashboard() {
  const { t, currentCase } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!currentCase) return;
    api.get(`/cases/${currentCase.id}/dashboard`).then(r => setData(r.data)).catch(() => {});
  }, [currentCase]);

  const runAI = async (prompt_key) => {
    if (!currentCase) return;
    setAiLoading(true);
    try {
      const result = await runAIPrompt({ prompt_key, case_id: currentCase.id, model: "claude" });
      toast.success(`AI: ${result.title} ✓`);
      navigate("/reports");
    } catch (e) {
      toast.error(e.message || "Σφάλμα AI");
    } finally { setAiLoading(false); }
  };

  if (!currentCase) return <div className="text-muted-foreground">{t("loading")}</div>;
  const c = currentCase;
  const counts = data?.counts || {};

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      {/* Hero */}
      <div className="surface-card p-7 lg:p-8 mb-6 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 size-64 rounded-full bg-[hsl(var(--gold)/0.06)] blur-3xl pointer-events-none" />
        <div className="flex flex-col lg:flex-row gap-8 items-start relative">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <StatusBadge tone="gold" testid="case-status-badge">{c.stage}</StatusBadge>
              <StatusBadge tone={c.urgency || "medium"}>{t(c.urgency || "medium")}</StatusBadge>
              <span className="text-[11px] text-muted-foreground font-mono">{c.case_number}</span>
            </div>
            <h1 className="font-heading font-semibold text-3xl lg:text-4xl tracking-tight leading-tight mb-3" data-testid="dashboard-case-title">{c.title}</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 mt-5">
              <Field label={t("client")} value={c.client_name}/>
              <Field label={t("court")} value={c.court}/>
              <Field label={t("next_hearing")} value={c.next_hearing_date ? new Date(c.next_hearing_date).toLocaleDateString("el-GR") : "—"}/>
              <Field label="Εισαγγελία" value={c.prosecutor || "—"}/>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-5">
              {(c.charges || []).map((ch, i) => (
                <span key={i} className="text-[11px] px-2 py-1 rounded bg-muted/40 border border-border/60 font-mono">{ch}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6 shrink-0 lg:min-w-[280px]">
            <Gauge value={c.risk_score} label={t("risk_score")} tone="risk" testid="risk-gauge"/>
            <Gauge value={c.defense_readiness} label={t("defense_readiness")} tone="gold" testid="readiness-gauge"/>
          </div>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label={t("total_witnesses")} value={counts.witnesses ?? "—"} icon={Users} testid="stat-witnesses"/>
        <StatCard label={t("total_evidence")} value={counts.evidence ?? "—"} icon={Scale} testid="stat-evidence"/>
        <StatCard label={t("pending_tasks")} value={counts.tasks ?? "—"} icon={ListChecks} testid="stat-tasks"/>
        <StatCard label={t("upcoming_deadlines")} value={counts.hearings ?? "—"} icon={Gavel} testid="stat-hearings"/>
      </div>

      {/* Quick actions */}
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-[15px] mb-3 flex items-center gap-2">
          <Sparkles className="size-4 gold-text"/>{t("quick_actions")}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <QuickAction label={t("add_document")} icon={Upload} testid="qa-add-document" onClick={() => navigate("/case")}/>
          <QuickAction label={t("add_witness")} icon={Users} testid="qa-add-witness" onClick={() => navigate("/witnesses")}/>
          <QuickAction label={t("add_event")} icon={Clock} testid="qa-add-event" onClick={() => navigate("/timeline")}/>
          <QuickAction label={t("generate_timeline")} icon={Clock} testid="qa-gen-timeline" onClick={() => runAI("timeline")}/>
          <QuickAction label={t("generate_trial_prep")} icon={FileText} testid="qa-trial-prep" onClick={() => runAI("trial_preparation")}/>
          <QuickAction label={t("generate_cross_exam")} icon={Sparkles} testid="qa-cross-exam" onClick={() => runAI("cross_examination")}/>
        </div>
        {aiLoading && <div className="text-[12px] text-muted-foreground font-mono mt-2 animate-pulse-soft">AI is thinking…</div>}
      </div>

      {/* Bento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upcoming hearings */}
        <Card title={t("upcoming_hearings")} icon={CalendarDays} testid="widget-hearings">
          {(data?.upcoming_hearings || []).length === 0 ? <Empty/> : (data.upcoming_hearings.map(h => (
            <Row key={h.id}
              left={<div className="text-[12.5px] font-medium">{h.purpose}</div>}
              right={<div className="text-[11px] font-mono text-muted-foreground">{new Date(h.date).toLocaleDateString("el-GR")}</div>}
              sub={h.court}/>
          )))}
        </Card>

        {/* Risky witnesses */}
        <Card title={t("risky_witnesses")} icon={AlertTriangle} testid="widget-risky-witnesses">
          {(data?.risky_witnesses || []).length === 0 ? <Empty/> : data.risky_witnesses.map(w => (
            <Row key={w.id}
              left={<div className="text-[12.5px] font-medium">{w.full_name}</div>}
              right={<StatusBadge tone={w.strength || "neutral"}>{t(w.strength || "neutral")}</StatusBadge>}
              sub={w.role}/>
          ))}
        </Card>

        {/* Recent AI */}
        <Card title={t("ai_insights")} icon={Sparkles} testid="widget-ai-insights">
          {(data?.recent_ai || []).length === 0 ? <Empty/> : data.recent_ai.map(o => (
            <Row key={o.id}
              left={<div className="text-[12.5px] font-medium truncate">{o.title}</div>}
              right={o.confidence != null ? <StatusBadge tone="gold">AI {o.confidence}%</StatusBadge> : <StatusBadge tone="gold">AI</StatusBadge>}
              sub={new Date(o.created_at).toLocaleString("el-GR")}/>
          ))}
        </Card>

        {/* Recent evidence */}
        <Card title={t("recent_evidence")} icon={Scale} testid="widget-recent-evidence">
          {(data?.recent_evidence || []).length === 0 ? <Empty/> : data.recent_evidence.map(e => (
            <Row key={e.id}
              left={<div className="text-[12.5px] font-medium truncate">{e.title}</div>}
              right={<span className="text-[10px] font-mono text-muted-foreground">{e.evidence_id}</span>}
              sub={e.type}/>
          ))}
        </Card>

        {/* Pending tasks */}
        <Card title={t("pending_tasks")} icon={ListChecks} testid="widget-pending-tasks" wide>
          {(data?.pending_tasks || []).length === 0 ? <Empty/> : data.pending_tasks.slice(0, 6).map(task => (
            <Row key={task.id}
              left={<div className="flex items-center gap-2"><StatusBadge tone={task.priority || "medium"}>{t(task.priority || "medium")}</StatusBadge><span className="text-[12.5px] font-medium truncate">{task.title}</span></div>}
              right={<span className="text-[11px] font-mono text-muted-foreground">{task.due_date ? new Date(task.due_date).toLocaleDateString("el-GR") : "—"}</span>}/>
          ))}
        </Card>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-mono mb-0.5">{label}</div>
      <div className="text-[13px] font-medium truncate">{value || "—"}</div>
    </div>
  );
}
function Card({ title, icon: Icon, children, wide, testid }) {
  return (
    <div className={`surface-card p-5 ${wide ? "lg:col-span-2" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-[13.5px] flex items-center gap-2"><Icon className="size-4 gold-text" strokeWidth={1.5}/>{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ left, right, sub }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/40 last:border-0 gap-3">
      <div className="min-w-0 flex-1">
        <div className="truncate">{left}</div>
        {sub && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{sub}</div>}
      </div>
      <div className="shrink-0">{right}</div>
    </div>
  );
}
function Empty() { return <div className="text-[12px] text-muted-foreground py-2">—</div>; }
